import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import axios from 'axios';
import { debounce, isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import { GeneralEmailCommunicationAiAnalysisColumnIndicator } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/GeneralEmailCommunicataionAiAnalysisColumnIndicator';
import { GeneralEmailCommunicationAiAnalysisBanner } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/GeneralEmailCommunicationAiAnalysisBanner';
import AlertBanner from '~/components/core/AlertBanner';
import Grid from '~/components/core/Atomic/Grid/Grid';
import EmptyState from '~/components/core/EmptyState';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { compareDatesStrings, serverDateTimeToLocal } from '~/DateTimeUtils';
import { MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import UNCLASSIFIED_EMAILS_STATUSES from '~/server_shared/generated-types/UNCLASSIFIED_EMAILS_STATUSES';
import { reportAxiosError, stringCmp } from '~/Utils';

import ViewCommunicationCardContainer from './communications/ViewCommunicationCardContainer';
import SkeletonTable from './core/Skeletons/SkeletonTable';
import { MoveToFnolInboxDialog } from './Fnol/FnolAiInbox/MoveToFnolInboxDialog/MoveToFnolInboxDialog';
import { useCms } from './hooks/useCms';
import { useCliveConfigOptions } from './SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import CardDialog from './CardDialog';
import { MultiSelectFilter, SortableTable } from './core';
import { AttachmentIcon } from './icons';
import { AllDoneIllustration } from './illustrations';
import useOrganization from './OrganizationContext';
import useDataFetcher from './useDataFetcher';

const GeneralEmailCommunicationsTab = () => {
  const { classes } = useStyles();
  const { setPageTitle, user, userOrganization } = useCms();
  const { emailDomains } = useOrganization();
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const { isGeneralEmailCliveRecommendationEnabled } = useCliveConfigOptions();

  const {
    isLoading,
    isError,
    data: unclassifiedEmails = [],
    reloadData,
  } = useDataFetcher(`/api/v1/organizations/${user.organization_id}/unclassified_emails`, {
    params: debouncedSearchText ? { search_text: debouncedSearchText } : {},
  });

  const isCliveRecommendationEnabled = isGeneralEmailCliveRecommendationEnabled();

  const [communicationId, setCommunicationId] = useState();
  const [selectedUnclassifiedEmailId, setSelectedUnclassifiedEmailId] = useState();
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [emailsToShow, setEmailsToShow] = useState([]);
  const [showMoveToFnolDialog, setShowMoveToFnolDialog] = useState(false);

  const isSubOrgDomainsEnabled = userOrganization.is_suborgs_domains_enabled;

  // Filter email domains based on user's sub-organization restrictions
  const userSubOrgIds = user.sub_organization_ids;
  const hasSubOrgRestrictions = userSubOrgIds && userSubOrgIds.length > 0;

  const emailDomainsStrings = hasSubOrgRestrictions
    ? emailDomains
        ?.filter((emailDomain) => {
          const domainSubOrgIds = emailDomain.sub_organization_ids || [];
          return domainSubOrgIds.some((subOrgId) => userSubOrgIds.includes(subOrgId));
        })
        .map((emailDomain) => emailDomain.domain)
    : emailDomains?.map((emailDomain) => emailDomain.domain);

  // Get IDs of emails with pending_async status to disable them
  const pendingAsyncEmailIds = useMemo(() => {
    return (
      emailsToShow
        ?.filter((email) => email.status === UNCLASSIFIED_EMAILS_STATUSES['PENDING_ASYNC'])
        .map((email) => email.id) || []
    );
  }, [emailsToShow]);

  // Get the currently selected unclassified email and check if it's pending_async
  const selectedUnclassifiedEmail = useMemo(() => {
    return unclassifiedEmails.find((email) => email.id === selectedUnclassifiedEmailId);
  }, [unclassifiedEmails, selectedUnclassifiedEmailId]);

  const isSelectedEmailPendingAsync = useMemo(() => {
    return selectedUnclassifiedEmail?.status === UNCLASSIFIED_EMAILS_STATUSES['PENDING_ASYNC'];
  }, [selectedUnclassifiedEmail]);

  const setDisplayedId = useCallback(
    (id) => {
      const unclassifiedEmail = unclassifiedEmails.find((email) => email.id === id);
      if (unclassifiedEmail) {
        setSelectedUnclassifiedEmailId(unclassifiedEmail.id);
        setCommunicationId(unclassifiedEmail.communication_id);
      }
    },
    [unclassifiedEmails]
  );

  const updateUnclassifiedEmails = useCallback((data) => {
    setSelectedUnclassifiedEmailId(undefined);
    setCommunicationId(undefined);
    !isEmpty(data) && data.sort((a, b) => compareDatesStrings(a?.arrival_date, b?.arrival_date));
  }, []);

  const reportAndReload = async (error) => {
    reportAxiosError(error);
    await reloadData();
  };

  const onUpdate = async () => {
    try {
      await reloadData();
    } catch (error) {
      reportAxiosError(error);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}`
      );
    } catch (error) {
      reportAndReload(error);
    }
  };

  const handleRefer = async (user_id) => {
    try {
      await axios.post(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}/refer_to_user/${user_id}`
      );
    } catch (error) {
      reportAndReload(error);
    }
  };

  const handleAttach = async (claim_id, attachExtraParams) => {
    try {
      await axios.post(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}/attach_to_claim/${claim_id}`,
        attachExtraParams
      );
    } catch (error) {
      reportAndReload(error);
    }
  };

  const handleForward = async (data) => {
    try {
      await axios.post(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}/forward`,
        data
      );
    } catch (error) {
      reportAndReload(error);
      throw error;
    }
  };

  const handleReply = async (data) => {
    try {
      await axios.post(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}/reply`,
        data
      );
    } catch (error) {
      reportAndReload(error);
    }
  };

  const handleMoveToFnolQueue = () => setShowMoveToFnolDialog(true);

  const handleMoveWithoutSelection = async () => {
    try {
      await axios.post(
        `/api/v1/organizations/${user.organization_id}/unclassified_emails/${selectedUnclassifiedEmailId}/move_to_fnol_queue`
      );
      setShowMoveToFnolDialog(false);
      await onUpdate();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const withUpdateDecorate = (func) => {
    return async (...args) => {
      const result = await func(...args);
      await onUpdate();
      return result;
    };
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        // Only update if search text has more than 3 characters or is empty (to clear results)
        if (searchValue.length >= 3 || searchValue.length === 0) {
          setDebouncedSearchText(searchValue);
        } else {
          setDebouncedSearchText('');
        }
      }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchText);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchText, debouncedSearch]);

  useEffect(() => {
    setPageTitle('General Mail Classification', 'General Mail Classification - Five Sigma CMS');
  }, [setPageTitle]);

  useEffect(() => {
    let filteredUnclassifiedEmails = unclassifiedEmails || [];

    if (isSubOrgDomainsEnabled && selectedDomains.length > 0 && unclassifiedEmails) {
      filteredUnclassifiedEmails = unclassifiedEmails.filter((unclassifiedEmail) =>
        selectedDomains.includes(unclassifiedEmail.email_domain.domain)
      );
    }
    if (!isLoading) {
      setEmailsToShow(filteredUnclassifiedEmails);
      updateUnclassifiedEmails(filteredUnclassifiedEmails);
    }
  }, [unclassifiedEmails, updateUnclassifiedEmails, selectedDomains, isSubOrgDomainsEnabled, isLoading]);

  const columnData = [
    {
      id: 'contact',
      numeric: false,
      label: 'Contact',
      overflow: true,
      maxWidthPercentage: 15,
      specialCell: (comm) =>
        comm.contact
          ? comm.contact.full_name +
            (comm.cc_contacts_ids && comm.cc_contacts_ids.length > 0 ? ` (+${comm.cc_contacts_ids.length})` : '')
          : comm.specific_identifier,
      specialCmpFunc: (comm1, comm2) =>
        stringCmp(
          comm1.contact?.full_name || comm1.specific_identifier || '',
          comm2.contact?.full_name || comm2.specific_identifier || ''
        ),
    },
    {
      id: 'subject',
      numeric: false,
      label: 'Subject',
      maxWidthPercentage: 25,
      overflow: true,
      specialCell: (comm) => comm.subject,
    },
    {
      id: 'arrival_date',
      numeric: false,
      rightPaddingOnly: true,
      label: 'Time',
      nowrap: true,
      specialCell: (comm) => serverDateTimeToLocal(comm.arrival_date),
    },
    {
      id: 'attachments',
      label: 'Attachment',
      specialCell: (comm) => (comm.attachments ? <AttachmentIcon /> : null),
      disableSort: true,
    },
  ];

  if (isSubOrgDomainsEnabled) {
    columnData.push({
      id: 'email_domain',
      numeric: false,
      label: 'Email Domain',
      specialCell: (comm) => comm.email_domain?.domain,
    });
  }

  // Only add the AI analysis column if Clive recommendations are enabled
  if (isCliveRecommendationEnabled) {
    columnData.push({
      id: 'analysis_extra',
      rightPaddingOnly: true,
      numeric: false,
      disableSort: true,
      label: "Clive's Analysis",
      nowrap: true,
      specialCell: (comm) => (
        <GeneralEmailCommunicationAiAnalysisColumnIndicator
          gqAnalysisExtra={comm.analysis_extra}
          cliveStoreInformation={comm.clive_store_information}
        />
      ),
    });
  }

  const orderByColumnIdx = columnData.findIndex((column) => column.id === 'arrival_date');
  const WarningMessage = () => (
    <Grid container direction="row" style={{ paddingBottom: '20px' }}>
      <Grid item xs={12}>
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          note="Please Note - Any action on an email will delete it from the queue."
          withIcon
        />
      </Grid>
    </Grid>
  );

  const displayLoading = isLoading && isEmpty(unclassifiedEmails);

  return (
    <div className={classes.pageBody}>
      <Grid container direction="row" style={{ padding: '20px' }}>
        {!isEmpty(unclassifiedEmails) && <WarningMessage />}
        <Grid item xs={3} style={{ marginBottom: '16px' }}>
          <TextField
            label="Search emails"
            placeholder="Search by subject, body, or sender email..."
            value={searchText}
            onChange={(value) => setSearchText(value)}
            fullWidth
            size="small"
            helperText={
              searchText.length > 0 && searchText.length <= 2 ? 'Enter at least 3 characters to search' : null
            }
          />
        </Grid>
        {isSubOrgDomainsEnabled && (
          <Grid item xs={12}>
            <MultiSelectFilter
              label="Email Domain"
              value={selectedDomains}
              onChange={setSelectedDomains}
              options={emailDomainsStrings}
              withOptionChips
            />
          </Grid>
        )}

        <PanelGroup direction="horizontal" id="general-mail-classification">
          <Panel id="table-display" defaultSize={70}>
            <CardDialog noCardTitle>
              {(displayLoading || isError) && (
                <SkeletonTable
                  rowsCount={15}
                  columnsCount={columnData.length}
                  maxHeight={`${window.innerHeight - 300}px`}
                  isError={isError}
                />
              )}
              {!displayLoading && (
                <SortableTable
                  rows={emailsToShow}
                  columns={columnData}
                  onRowClick={setDisplayedId}
                  selectedRowsIds={[selectedUnclassifiedEmailId]}
                  greyedOutRowsIds={pendingAsyncEmailIds}
                  maxHeight={`${window.innerHeight - 300}px`}
                  autoPaginateRowsPerPage={15}
                  defaultOrderColumn={orderByColumnIdx}
                  order="desc"
                  stickyHeader
                  emptyStateComponent={
                    <EmptyState
                      title={debouncedSearchText ? 'No results found' : 'All Done!'}
                      subtitle={
                        debouncedSearchText ? 'Consider trying different keywords' : 'No new mails were received.'
                      }
                      illustration={debouncedSearchText ? undefined : <AllDoneIllustration />}
                    />
                  }
                />
              )}
            </CardDialog>
          </Panel>
          {communicationId && !isSelectedEmailPendingAsync ? (
            <>
              <PanelResizeHandle id="resize-handle" className="mx-12 w-2 rounded-lg bg-slate-500" />
              <Panel id="communication-display" style={{ overflow: 'auto' }} minSize={35} maxSize={80}>
                <CardDialog
                  headerStyle={{ padding: '0' }}
                  contentStyle={{ padding: '8px 0 0 0' }}
                  containerStyle={{
                    marginLeft: '20px',
                    maxHeight: `${window.innerHeight - 248}px`,
                    overflow: 'auto',
                  }}
                  noCardTitle
                >
                  <ViewCommunicationCardContainer
                    key={communicationId}
                    communicationId={communicationId}
                    onUpdate={onUpdate}
                    displayAttachClaim={false}
                    displayGeneralQueueActions={!isSelectedEmailPendingAsync}
                    onDelete={isSelectedEmailPendingAsync ? undefined : handleDelete}
                    onRefer={isSelectedEmailPendingAsync ? undefined : handleRefer}
                    onAttach={isSelectedEmailPendingAsync ? undefined : handleAttach}
                    onForward={isSelectedEmailPendingAsync ? undefined : handleForward}
                    onReply={isSelectedEmailPendingAsync ? undefined : handleReply}
                    onMoveToFnolQueue={isSelectedEmailPendingAsync ? undefined : handleMoveToFnolQueue}
                    lifeTimeMixpanelEvent={MIXPANEL_EVENTS.GQ_VIEW_EMAIL_COMMUNICATION_LIFETIME}
                    BannerBelowHeader={
                      selectedUnclassifiedEmail?.analysis_extra ? (
                        <GeneralEmailCommunicationAiAnalysisBanner
                          communicationType="email"
                          communicationId={communicationId}
                          communicationAiAnalysis={selectedUnclassifiedEmail.analysis_extra}
                          communicationAiAnalysisActions={
                            isSelectedEmailPendingAsync
                              ? undefined
                              : {
                                  onAttachToClaim: withUpdateDecorate(handleAttach),
                                  onMoveToFnolQueue: handleMoveToFnolQueue,
                                }
                          }
                        />
                      ) : null
                    }
                  />
                </CardDialog>
              </Panel>
            </>
          ) : null}
        </PanelGroup>
      </Grid>
      {showMoveToFnolDialog && selectedUnclassifiedEmailId && (
        <MoveToFnolInboxDialog
          unclassifiedEmailId={selectedUnclassifiedEmailId}
          onClose={() => setShowMoveToFnolDialog(false)}
          onMoved={onUpdate}
          onMoveWithoutSelection={handleMoveWithoutSelection}
        />
      )}
    </div>
  );
};

export default GeneralEmailCommunicationsTab;
